class DropdownMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      topMenuItem: '.header__menu-item--top',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.list-menu-dropdown > .list-menu',
      trigger: '.header__menu-item--top',
      dropdown: '.list-menu-dropdown',
      focusableItems: 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    };
    this.timeout;

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    this.addEventListener('focusin', this.handleFocusin.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  connectedCallback() {
    this.setupAccessibility();
  }

  setupAccessibility() {
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger) {
      const dropdown = this.querySelector(this.selectors.dropdown);
      const hasDropdown = dropdown !== null;

      // Make trigger focusable if not already
      if (!trigger.hasAttribute('tabindex') && trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A') {
        trigger.setAttribute('tabindex', '0');
      }
      // Add ARIA attributes only if there's a dropdown
      if (!hasDropdown) return;
      trigger.setAttribute('aria-haspopup', hasDropdown ? 'true' : 'false');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('role', 'button');
    }
  }

  handleFocusin(event) {
    // Only activate on trigger focus, not on dropdown content focus
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger && (event.target === trigger || trigger.contains(event.target))) {
      // Dropdown now opens on Enter/Space, not on focus
    }
  }

  handleKeydown(event) {
    const trigger = this.querySelector(this.selectors.trigger);
    const dropdown = this.querySelector(this.selectors.dropdown);
    const isOpen = this.classList.contains(this.classes.active);

    switch (event.key) {
      case 'Enter':
      case ' ':
        // Only handle if trigger is focused AND there's a dropdown
        if (event.target === trigger || trigger?.contains(event.target)) {
          // If there's no dropdown, let the native link behavior happen
          if (!dropdown) {
            return;
          }
          event.preventDefault();
          if (isOpen) {
            this.close();
            trigger?.focus();
          } else {
            this.handleMouseover();
            // Move focus to first item in dropdown
            this.focusFirstItem();
          }
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          trigger?.focus();
        }
        break;

      case 'ArrowDown':
        if (isOpen) {
          event.preventDefault();
          this.focusNextItem();
        } else if (event.target === trigger || trigger?.contains(event.target)) {
          event.preventDefault();
          this.handleMouseover();
          this.focusFirstItem();
        }
        break;

      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          this.focusPreviousItem();
        }
        break;

      case 'Tab':
        // Allow natural tab behavior but close menu when tabbing out
        if (isOpen) {
          const focusableItems = this.getFocusableItems();
          const lastItem = focusableItems[focusableItems.length - 1];
          const firstItem = focusableItems[0];
          
          if (event.shiftKey && document.activeElement === firstItem) {
            // Tabbing backwards from first item - close and focus trigger
            this.close();
          } else if (!event.shiftKey && document.activeElement === lastItem) {
            // Tabbing forward from last item - close menu
            this.close();
          }
        }
        break;
    }
  }

  getFocusableItems() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    if (!dropdown) return [];
    return Array.from(dropdown.querySelectorAll(this.selectors.focusableItems));
  }

  focusFirstItem() {
    requestAnimationFrame(() => {
      const items = this.getFocusableItems();
      if (items.length > 0) {
        items[0].focus();
      }
    });
  }

  focusNextItem() {
    const items = this.getFocusableItems();
    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    items[nextIndex]?.focus();
  }

  focusPreviousItem() {
    const items = this.getFocusableItems();
    const currentIndex = items.indexOf(document.activeElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    items[prevIndex]?.focus();
  }

  updateAriaExpanded(expanded) {
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;

    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.resetBackground();
    this.animateMenu();
    this.updateAriaExpanded(true);
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();
    this.updateAriaExpanded(false);

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);

    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  addClasses() {
    const isTopLevel = this.querySelector(this.selectors.topMenuItem);

    let delay = 0;
    if (isTopLevel && !this.isHover()) {
      delay = 150;

      if (this.closest('.header--top-center') || this.closest('.header--top-left')) {
        delay = 200;
      }
    }
    this.timeout = setTimeout(() => {
      if (this.isHover()) {
        this.classList.add(this.classes.active);
      }
    }, delay);

    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);

    menus.forEach((menu) => {
      let found = false;
      const childMenus = menu.querySelectorAll('dropdown-menu');
      childMenus.forEach((element) => {
        if (element === this) {
          found = true;
          return;
        }
      });

      if (!found) menu.reset();
    });
  }

  isHover() {
    return this.header.classList.contains(this.classes.hover);
  }

  reset() {
    this.classList.remove(this.classes.active);
    this.updateAriaExpanded(false);

    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) childAnimate.classList.remove(this.classes.animate);

    clearTimeout(this.timeout);
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  animateMenu() {
    const childAnimate =  this.querySelector(this.selectors.childAnimate);
    if (childAnimate) {
      setTimeout(() => {
        childAnimate.classList.add(this.classes.animate);
      }, 150);
    }
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('dropdown-menu', DropdownMenu);

class MegaMenu extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      hover: 'is-hover',
      active: 'is-active',
      animate: 'show-from-bottom',
    };
    this.selectors = {
      header: '.shopify-section-header',
      background: '.header-background',
      dropdown: '.list-menu-dropdown',
      menus: 'dropdown-menu, mega-menu',
      wrapper: '.list-menu',
      childAnimate: '.mega-menu__item, .mega-menu__promo',
      trigger: '.header__menu-item--top',
      focusableItems: 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    };
    this.timeout = [];

    this.addEventListener('mouseenter', this.handleMouseover.bind(this));
    this.addEventListener('focusin', this.handleFocusin.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  connectedCallback() {
    this.setupAccessibility();
  }

  setupAccessibility() {
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger) {
      // Make trigger focusable if not already
      if (!trigger.hasAttribute('tabindex') && trigger.tagName !== 'BUTTON' && trigger.tagName !== 'A') {
        trigger.setAttribute('tabindex', '0');
      }
      // Add ARIA attributes
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('role', 'button');
    }
  }

  handleFocusin(event) {
    // Only activate on trigger focus, not on dropdown content focus
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger && (event.target === trigger || trigger.contains(event.target))) {
      // Dropdown now opens on Enter/Space, not on focus
    }
  }

  handleKeydown(event) {
    const trigger = this.querySelector(this.selectors.trigger);
    const dropdown = this.querySelector(this.selectors.dropdown);
    const isOpen = this.classList.contains(this.classes.active);

    switch (event.key) {
      case 'Enter':
      case ' ':
        // Only handle if trigger is focused
        if (event.target === trigger || trigger?.contains(event.target)) {
          event.preventDefault();
          if (isOpen) {
            this.close();
            trigger?.focus();
          } else {
            this.handleMouseover();
            // Move focus to first item in dropdown
            this.focusFirstItem();
          }
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
          trigger?.focus();
        }
        break;

      case 'ArrowDown':
        if (isOpen) {
          event.preventDefault();
          this.focusNextItem();
        } else if (event.target === trigger || trigger?.contains(event.target)) {
          event.preventDefault();
          this.handleMouseover();
          this.focusFirstItem();
        }
        break;

      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          this.focusPreviousItem();
        }
        break;

      case 'ArrowRight':
        if (isOpen) {
          event.preventDefault();
          this.focusNextColumn();
        }
        break;

      case 'ArrowLeft':
        if (isOpen) {
          event.preventDefault();
          this.focusPreviousColumn();
        }
        break;

      case 'Tab':
        // Allow natural tab behavior but close menu when tabbing out
        if (isOpen) {
          const focusableItems = this.getFocusableItems();
          const lastItem = focusableItems[focusableItems.length - 1];
          const firstItem = focusableItems[0];
          
          if (event.shiftKey && document.activeElement === firstItem) {
            // Tabbing backwards from first item - close and focus trigger
            this.close();
          } else if (!event.shiftKey && document.activeElement === lastItem) {
            // Tabbing forward from last item - close menu
            this.close();
          }
        }
        break;
    }
  }

  getFocusableItems() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    if (!dropdown) return [];
    return Array.from(dropdown.querySelectorAll(this.selectors.focusableItems));
  }

  getColumnsAndItems() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    if (!dropdown) return [];
    
    // Get all mega menu columns
    const columns = Array.from(dropdown.querySelectorAll('.mega-menu__item, .mega-menu__promo, [style*="border-radius"]'));
    return columns.map(col => Array.from(col.querySelectorAll(this.selectors.focusableItems)));
  }

  getCurrentColumnAndIndex() {
    const columns = this.getColumnsAndItems();
    for (let colIndex = 0; colIndex < columns.length; colIndex++) {
      const itemIndex = columns[colIndex].indexOf(document.activeElement);
      if (itemIndex !== -1) {
        return { colIndex, itemIndex, columns };
      }
    }
    return { colIndex: -1, itemIndex: -1, columns };
  }

  focusFirstItem() {
    requestAnimationFrame(() => {
      const items = this.getFocusableItems();
      if (items.length > 0) {
        items[0].focus();
      }
    });
  }

  focusNextItem() {
    const items = this.getFocusableItems();
    const currentIndex = items.indexOf(document.activeElement);
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    items[nextIndex]?.focus();
  }

  focusPreviousItem() {
    const items = this.getFocusableItems();
    const currentIndex = items.indexOf(document.activeElement);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    items[prevIndex]?.focus();
  }

  focusNextColumn() {
    const { colIndex, itemIndex, columns } = this.getCurrentColumnAndIndex();
    if (colIndex === -1 || columns.length === 0) return;
    
    const nextColIndex = colIndex < columns.length - 1 ? colIndex + 1 : 0;
    const nextColumn = columns[nextColIndex];
    if (nextColumn && nextColumn.length > 0) {
      const targetIndex = Math.min(itemIndex, nextColumn.length - 1);
      nextColumn[targetIndex]?.focus();
    }
  }

  focusPreviousColumn() {
    const { colIndex, itemIndex, columns } = this.getCurrentColumnAndIndex();
    if (colIndex === -1 || columns.length === 0) return;
    
    const prevColIndex = colIndex > 0 ? colIndex - 1 : columns.length - 1;
    const prevColumn = columns[prevColIndex];
    if (prevColumn && prevColumn.length > 0) {
      const targetIndex = Math.min(itemIndex, prevColumn.length - 1);
      prevColumn[targetIndex]?.focus();
    }
  }

  updateAriaExpanded(expanded) {
    const trigger = this.querySelector(this.selectors.trigger);
    if (trigger) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  handleMouseover() {
    if (this.classList.contains(this.classes.active)) return;
    
    this.bindEvent();
    this.removeClasses();
    this.addClasses();
    this.showSublist();
    this.updateAriaExpanded(true);
  }

  handleMouseleave() {
    if (!this.header) return;

    this.reset();
    this.resetBackground();
    this.updateAriaExpanded(false);

    this.header.classList.remove(this.classes.hover);
    this.wrapper.removeEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
  }

  handleTouchMoveOutside(ev) {
    if (!this.header) return;
    
    let target_section = ev.targetTouches[0].target.closest(this.selectors.header);
    if(!target_section || this.header.classList.contains('shopify-section-header-hidden')) {
      this.reset();
      this.resetBackground();
      this.updateAriaExpanded(false);
      
      this.header.classList.remove(this.classes.hover);
      document.removeEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
    }
  }

  bindEvent() {
    this.onHeaderMouseLeaveEvent = this.onHeaderMouseLeaveEvent || this.handleMouseleave.bind(this);
    this.onHeaderTouchMoveOutsideEvent = this.onHeaderTouchMoveOutsideEvent || this.handleTouchMoveOutside.bind(this);
    
    this.header = this.header || document.querySelector(this.selectors.header);

    this.wrapper = this.closest(this.selectors.wrapper)
    this.wrapper.addEventListener('mouseleave', this.onHeaderMouseLeaveEvent);
    document.addEventListener('touchmove', this.onHeaderTouchMoveOutsideEvent);
  }

  showSublist() {
    const dropdown = this.querySelector(this.selectors.dropdown);
    const background = this.header.querySelector(this.selectors.background);
    background.style.setProperty('--height', this.header.clientHeight + dropdown.clientHeight + 'px');
    background.classList.add(this.classes.active);
  }

  addClasses() {
    this.classList.add(this.classes.active);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    const delay = childAnimate.length > 5 ? 75 : 150;
    childAnimate.forEach((element, index) => {
      const timeout = setTimeout(() => {
        element.classList.add(this.classes.animate);
      }, (delay * index) + 100);
      
      this.timeout.push(timeout);
    });
    
    this.header.classList.add(this.classes.hover);
  }

  removeClasses() {
    const menus = this.header.querySelectorAll(this.selectors.menus);
    menus.forEach((menu) => {
      menu.reset();
    });
  }

  reset() {
    this.classList.remove(this.classes.active);
    this.updateAriaExpanded(false);

    const childAnimate = this.querySelectorAll(this.selectors.childAnimate);
    childAnimate.forEach((element) => {
      element.classList.remove(this.classes.animate);
    });
    this.timeout.forEach((timeout) => {
      clearTimeout(timeout);
    });
  }

  resetBackground() {
    const background = this.header.querySelector(this.selectors.background);
    background.classList.remove(this.classes.active);
  }

  close() {
    this.handleMouseleave();
  }
}
customElements.define('mega-menu', MegaMenu);
